import { Component, OnInit, ViewChild } from '@angular/core';
import { ConnectedEmailService } from 'app/services/connected-email.service';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ConnectedEmail } from 'app/models/connected-email';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-connected-emails',
  templateUrl: './connected-emails.component.html',
  styleUrls: ['./connected-emails.component.scss']
})
export class ConnectedEmailsComponent implements OnInit {

  tableLimitSelect = [5, 10, 20, 30, 50, 100];
  tableLoadingIndicator: boolean = true;
  selectedTableLimit = 5;

  connectedEmails: ConnectedEmail[] = [];
  connectedEmailForm: FormGroup;

  emailToDelete: ConnectedEmail = null;
  isDeleting: boolean = false;

  isAddingEmail: boolean = false;
  isAuthorizing: boolean = false;

  @ViewChild('addEditEmailModal') addEditEmailModal: ModalDirective;
  @ViewChild('deleteConfirmationModal') deleteConfirmationModal: ModalDirective;

  constructor(private connectedEmailService: ConnectedEmailService,
              private fb: FormBuilder)
            {
              window.addEventListener('storage', this.handleStorageEvent.bind(this));
            }

  ngOnInit(): void {
    this.loadConnectedEmails();
  }

  loadConnectedEmails(): void {
    this.tableLoadingIndicator = true;
    this.connectedEmailService.getAll().pipe(finalize(() => {
      this.tableLoadingIndicator = false;
    })) .subscribe(data => {
      this.connectedEmails = data;
    });
  }

  initForm(connectedEmail: ConnectedEmail = null): void {
    this.connectedEmailForm = this.fb.group({
      id: [connectedEmail?.id || null],
      email: [connectedEmail?.email || '', []],
      full_name: [connectedEmail?.full_name || '', []],
      provider: [connectedEmail?.provider || '', [Validators.required]],
   
      imap_server: [connectedEmail?.imap_server || null], 
      imap_port: [connectedEmail?.imap_port || 993], 
      imap_username: [connectedEmail?.imap_username || '', []],
      imap_password: [connectedEmail?.imap_password || null], 
   
      smtp_server: [connectedEmail?.smtp_server || null], 
      smtp_port: [connectedEmail?.smtp_port || 587], 
      smtp_username: [connectedEmail?.smtp_username || '', []],
      smtp_password: [connectedEmail?.smtp_password || null],

      authorization_code: [connectedEmail?.authorization_code || null],
      redirect_uri: [null],
    });
  }

  innerWidth(): number {
    return window.innerWidth;
  }

  openModal(connectedEmail: ConnectedEmail = null): void {
    this.initForm(connectedEmail);
    
    this.addEditEmailModal.show();
  }

  deleteConnectedEmail(): void {
    this.isDeleting = true;
    this.connectedEmailService.delete(this.emailToDelete.id).pipe(finalize(() => {
      this.isDeleting = false;
    })).subscribe(() => {
      this.connectedEmails = this.connectedEmails.filter(s => s.id !== this.emailToDelete.id);
      this.connectedEmails = [...this.connectedEmails];
      this.deleteConfirmationModal.hide();
    });
  }

  // Function to handle the provider change
  changeProvider(provider: string) {
    // Set the provider in the form group
    this.connectedEmailForm.get('provider').setValue(provider);

    // If OAuth providers (Microsoft or Google) are selected, then initiate OAuth flow
    if (provider === 'microsoft' || provider === 'google') {
      // Trigger OAuth2 flow
      this.startOAuth2Flow(provider);
    } else {
      // For other providers, OAuth2 flow is not necessary
      this.isAuthorizing = false;
    }
  }

  handleStorageEvent(e: StorageEvent) {
    if (e.key == 'ecodocs_connected_email' && e.newValue) {
      try {
        console.log('Authorization code received: ' + e.newValue);
        this.connectedEmailForm.get('authorization_code').setValue(e.newValue);
        this.isAuthorizing = false;
        this.onFinishedAddEditEmail();
      } catch (error) {
        console.log(error);
      }
    }
  };

  startOAuth2Flow(provider: string) {
    const redirectUri = 'http://localhost:4200/connected-emails/redirect';

    this.isAuthorizing = true;

    localStorage.removeItem('ecodocs_connected_email');
    this.connectedEmailForm.get('redirect_uri').setValue(redirectUri);

    if (provider === 'microsoft') {
      const scopes = 'email openid profile offline_access https://outlook.office.com/IMAP.AccessAsUser.All https://outlook.office.com/SMTP.Send'.replace(' ', '%20');
      window.open(`https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=0d4c3787-d9d7-4864-95e2-f58d7bf2cf2d&response_type=code&redirect_uri=${redirectUri}&response_mode=query&scope=${scopes}&state=microsoft&prompt=consent`, '_blank');
    } else if (provider === 'google') {
      window.open(`https://accounts.google.com/o/oauth2/v2/auth?client_id=746303063289-tlruv7pme9tckvi35qptq31jb3g8rf4t.apps.googleusercontent.com&access_type=offline&prompt=consent&redirect_uri=${redirectUri}&response_type=code&scope=email%20profile%20https://www.googleapis.com/auth/userinfo.email%20https://mail.google.com/%20https://www.googleapis.com/auth/gmail.modify%20https://www.googleapis.com/auth/gmail.send&state=google`, '_blank');
    }
  }

  onFinishedAddEditEmail() {
    if (this.connectedEmailForm.valid) {
      const data: ConnectedEmail = this.connectedEmailForm.value;
      if (data.id) {
        this.isAddingEmail = true;
        this.connectedEmailService.update(data.id, data).pipe(finalize(() => {
          this.isAddingEmail = false;
        })).subscribe(updatedEmail => {
          const index = this.connectedEmails.findIndex(s => s.id === updatedEmail.id);
          this.connectedEmails[index] = updatedEmail;
          this.connectedEmails = [...this.connectedEmails];
          this.addEditEmailModal.hide();
        });
      } else {
        this.isAddingEmail = true;
        this.connectedEmailService.create(data).pipe(finalize(() => {
          this.isAddingEmail = false;
        })).subscribe(newEmail => {
          this.connectedEmails.push(newEmail);
          this.connectedEmails = [...this.connectedEmails];
          this.addEditEmailModal.hide();
        });
      }
    }
  }
}
